import React, { useState, useEffect, useCallback } from 'react';
import {
  ComposedChart, Line, Area, Scatter, XAxis, YAxis, CartesianGrid, Tooltip,
  ReferenceLine, ResponsiveContainer, Legend,
} from 'recharts';
import { History, ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { hardwareHistoryAPI } from '../../services/api';

// Customer Hardware History (backend services/hardwareHistoryService): RX power graph, every
// ONU the customer has had, and a timeline of hardware events + outages. Collapsed by default
// and only fetches when opened, so it never slows the customer page down.

const RANGES = ['24h', '7d', '30d', '1y'];
const COLORS = ['var(--cl-2563eb)', 'var(--cl-16a34a)', 'var(--cl-9333ea)', 'var(--cl-ea580c)', 'var(--cl-0891b2)'];
const EVENT_TEXT = {
  baseline: 'Recording started',
  assigned: 'ONU assigned to this customer',
  unassigned: 'ONU no longer with this customer',
  moved: 'ONU moved',
  deregister: 'Deregistered',
  offline: 'Offline',
};

const fmtTime = (t, withDate = true) => new Date(t).toLocaleString('en-IN', withDate
  ? { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }
  : { hour: '2-digit', minute: '2-digit' });
const fmtDay = t => new Date(t).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
const dayToTs = day => new Date(`${day}T12:00:00+05:30`).getTime();

function ChartTooltip({ active, payload, long }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div style={{ background: 'var(--cb-ffffff)', border: '1px solid var(--cb-e5e7eb)', borderRadius: 6, padding: '6px 10px', fontSize: '.78rem' }}>
      <div style={{ fontWeight: 700, marginBottom: 2 }}>{long ? fmtDay(p.x) : fmtTime(p.x)}</div>
      {p.reason !== undefined ? (
        <div style={{ color: 'var(--c-dc2626)' }}>Deregistered{p.reason ? `: ${p.reason}` : ''}</div>
      ) : long ? (
        p.avg == null ? <div style={{ color: 'var(--c-9ca3af)' }}>offline all day</div> : (
          <div>avg {p.avg} dBm <span style={{ color: 'var(--c-6b7280)' }}>(low {p.band[0]}, high {p.band[1]})</span></div>
        )
      ) : (
        p.rx == null ? <div style={{ color: 'var(--c-9ca3af)' }}>offline</div> : (
          <div>ONU RX {p.rx} dBm{p.rxOlt != null && <span style={{ color: 'var(--c-6b7280)' }}> · OLT RX {p.rxOlt} dBm</span>}</div>
        )
      )}
    </div>
  );
}

function PowerChart({ data }) {
  const long = data.range === '30d' || data.range === '1y';
  const th = data.thresholds || {};
  const lines = data.series.map((s, i) => ({
    serial: s.serial,
    color: COLORS[i % COLORS.length],
    points: long
      ? s.points.map(p => ({ x: dayToTs(p.day), avg: p.avg, band: p.min == null ? null : [p.min, p.max] }))
      : s.points.map(p => ({ x: new Date(p.t).getTime(), rx: p.rx, rxOlt: p.rxOlt })),
  }));
  const dereg = data.events
    .filter(e => e.type === 'deregister')
    .map(e => ({ x: new Date(e.at).getTime(), y: th.minRxPower ?? -30, reason: e.reason || '' }));
  const hasPoints = lines.some(l => l.points.length);
  if (!hasPoints) {
    return <div style={{ padding: 24, textAlign: 'center', color: 'var(--c-9ca3af)', fontSize: '.85rem' }}>No power readings in this range yet — readings are saved every 15 minutes.</div>;
  }
  const now = Date.now();
  const from = new Date(data.from).getTime();
  return (
    <ResponsiveContainer width="100%" height={260}>
      <ComposedChart margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
        <CartesianGrid stroke="var(--cl-f3f4f6)" />
        <XAxis
          dataKey="x" type="number" scale="time" domain={[from, now]}
          tickFormatter={t => (data.range === '24h' ? fmtTime(t, false) : fmtDay(t))}
          tick={{ fontSize: 11 }} allowDuplicatedCategory={false}
        />
        <YAxis domain={['auto', 'auto']} tick={{ fontSize: 11 }} unit=" dBm" width={70} />
        <Tooltip content={<ChartTooltip long={long} />} />
        {lines.length > 1 && <Legend wrapperStyle={{ fontSize: 11 }} />}
        {th.goodRxMin != null && <ReferenceLine y={th.goodRxMin} stroke="var(--cl-d97706)" strokeDasharray="5 4" label={{ value: 'weak', fontSize: 10, fill: 'var(--cl-d97706)', position: 'insideBottomLeft' }} />}
        {th.minRxPower != null && <ReferenceLine y={th.minRxPower} stroke="var(--cl-dc2626)" strokeDasharray="5 4" label={{ value: 'critical', fontSize: 10, fill: 'var(--cl-dc2626)', position: 'insideBottomLeft' }} />}
        {th.maxRxPower != null && <ReferenceLine y={th.maxRxPower} stroke="var(--cl-dc2626)" strokeDasharray="5 4" label={{ value: 'too high', fontSize: 10, fill: 'var(--cl-dc2626)', position: 'insideTopLeft' }} />}
        {lines.map(l => (long ? [
          <Area key={`${l.serial}-band`} data={l.points} dataKey="band" stroke="none" fill={l.color} fillOpacity={0.15} connectNulls={false} isAnimationActive={false} legendType="none" />,
          <Line key={`${l.serial}-avg`} data={l.points} dataKey="avg" name={l.serial} stroke={l.color} strokeWidth={2} dot={false} connectNulls={false} isAnimationActive={false} />,
        ] : (
          <Line key={l.serial} data={l.points} dataKey="rx" name={l.serial} stroke={l.color} strokeWidth={1.5} dot={false} connectNulls={false} isAnimationActive={false} />
        )))}
        {dereg.length > 0 && <Scatter data={dereg} dataKey="y" fill="var(--cl-dc2626)" name="Deregister" legendType="none" isAnimationActive={false} />}
      </ComposedChart>
    </ResponsiveContainer>
  );
}

export default function HardwareHistoryPanel({ customerId, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const [range, setRange] = useState('7d');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    hardwareHistoryAPI.getCustomer(customerId, range)
      .then(({ data: res }) => setData(res.data))
      .catch(err => setError(err.response?.data?.message || 'Could not load hardware history'))
      .finally(() => setLoading(false));
  }, [customerId, range]);

  useEffect(() => { if (open) load(); }, [open, load]);

  return (
    <div style={{ background: 'var(--cb-ffffff)', border: '1px solid var(--cb-e5e7eb)', borderRadius: 8, marginBottom: 12, overflow: 'hidden' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '12px 20px', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: '.88rem', color: 'var(--c-374151)', font: 'inherit' }}
      >
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <History size={14} color="var(--accent)" /> Hardware History
        <span style={{ fontWeight: 400, color: 'var(--c-9ca3af)', fontSize: '.78rem' }}>RX power, ONUs, drops</span>
      </button>

      {open && (
        <div style={{ borderTop: '1px solid var(--cb-e5e7eb)', padding: '12px 20px 16px' }}>
          <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
            {RANGES.map(r => (
              <button
                key={r} type="button" onClick={() => setRange(r)}
                style={{
                  font: 'inherit', fontSize: '.78rem', padding: '4px 12px', borderRadius: 14, cursor: 'pointer', fontWeight: 600,
                  border: `1.5px solid ${range === r ? 'var(--primary)' : 'var(--cb-e5e7eb)'}`,
                  background: range === r ? 'var(--primary)' : 'var(--cb-ffffff)', color: range === r ? 'var(--c-ffffff)' : 'var(--c-374151)',
                }}
              >{r}</button>
            ))}
            {loading && <Loader2 size={16} style={{ animation: 'spin .7s linear infinite', alignSelf: 'center' }} />}
          </div>

          {error && <div style={{ color: 'var(--c-dc2626)', fontSize: '.85rem' }}>{error}</div>}

          {data && !error && (data.onus.length === 0 ? (
            <div style={{ padding: 16, color: 'var(--c-9ca3af)', fontSize: '.85rem' }}>
              No hardware history for this customer yet. It starts recording once their ONU is labelled with their PPPoE username on the OLT.
            </div>
          ) : (
            <>
              <div style={{ fontSize: '.78rem', fontWeight: 700, color: 'var(--c-6b7280)', marginBottom: 4 }}>RX power (at the ONU)</div>
              <PowerChart data={data} />

              {/* The device in service now is shown once, in Current Hardware; this list is only the earlier ones. */}
              <div style={{ fontSize: '.78rem', fontWeight: 700, color: 'var(--c-6b7280)', margin: '14px 0 6px' }}>Previous devices</div>
              {(() => {
                const cur = data.onus.find(o => o.current);
                const prev = data.onus.filter(o => !o.current);
                return (
                  <>
                    {cur && <div style={{ fontSize: '.78rem', color: 'var(--c-6b7280)', marginBottom: 6 }}>Current ONU has been on this connection since {fmtTime(cur.from)}.</div>}
                    {prev.length === 0 && <div style={{ color: 'var(--c-9ca3af)', fontSize: '.82rem' }}>No earlier devices recorded.</div>}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {prev.map(o => {
                        const evs = data.events.filter(e => e.serial === o.serial);
                        return (
                          <details key={`${o.serial}-${o.from}`} style={{ border: '1px solid var(--cb-f3f4f6)', borderRadius: 6, fontSize: '.82rem' }}>
                            <summary style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', padding: '6px 10px', cursor: 'pointer' }}>
                              <span style={{ fontFamily: 'monospace', fontWeight: 700 }}>{o.serial}</span>
                              <span style={{ color: 'var(--c-6b7280)' }}>{o.model || '—'}</span>
                              <span style={{ color: 'var(--c-6b7280)' }}>{fmtTime(o.from)} → {o.to ? fmtTime(o.to) : '—'}</span>
                            </summary>
                            <div style={{ padding: '6px 10px 10px', display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 12px', borderTop: '1px solid var(--cb-f3f4f6)' }}>
                              <span style={{ color: 'var(--c-6b7280)' }}>Serial</span><span style={{ fontFamily: 'monospace' }}>{o.serial}</span>
                              <span style={{ color: 'var(--c-6b7280)' }}>Model</span><span>{o.model || '—'}</span>
                              <span style={{ color: 'var(--c-6b7280)' }}>ONU ID</span><span>{o.onuId || '—'}</span>
                              <span style={{ color: 'var(--c-6b7280)' }}>Installed</span><span>{fmtTime(o.from)}</span>
                              <span style={{ color: 'var(--c-6b7280)' }}>Removed</span><span>{o.to ? fmtTime(o.to) : '—'}</span>
                              <span style={{ color: 'var(--c-6b7280)' }}>Events</span>
                              <span>{evs.length ? evs.map((e, i) => <div key={i}>{fmtTime(e.at)} · {EVENT_TEXT[e.type] || e.type}{e.reason ? ` — ${e.reason}` : ''}</div>) : 'None in this range'}</span>
                            </div>
                          </details>
                        );
                      })}
                    </div>
                  </>
                );
              })()}

              <div style={{ fontSize: '.78rem', fontWeight: 700, color: 'var(--c-6b7280)', margin: '14px 0 6px' }}>Hardware events (outages are under Uptime)</div>
              {data.events.filter(e => e.type !== 'offline').length === 0 ? (
                <div style={{ color: 'var(--c-9ca3af)', fontSize: '.82rem' }}>No events in this range.</div>
              ) : (
                <div style={{ maxHeight: 260, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {data.events.filter(e => e.type !== 'offline').map((e, i) => (
                    <div key={i} style={{ display: 'flex', gap: 10, fontSize: '.8rem', padding: '4px 0', borderTop: i ? '1px solid var(--cb-f3f4f6)' : 'none' }}>
                      <span style={{ color: 'var(--c-6b7280)', minWidth: 118 }}>{fmtTime(e.at)}</span>
                      <span style={{ color: e.type === 'deregister' || e.type === 'offline' ? 'var(--c-dc2626)' : 'var(--c-374151)', fontWeight: 600 }}>
                        {EVENT_TEXT[e.type] || e.type}
                        {e.reason ? ` — ${e.reason}` : ''}
                        {e.type === 'offline' && (e.active ? ' (still offline)' : e.until ? ` until ${fmtTime(e.until)}` : '')}
                        {e.type === 'moved' && ` ${e.fromOnuId} → ${e.toOnuId}`}
                      </span>
                      <span style={{ color: 'var(--c-9ca3af)', fontFamily: 'monospace' }}>{e.serial}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          ))}
        </div>
      )}
    </div>
  );
}
